import { useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
} from "../components/ui.js";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../hooks/useInventory.js";
import type { Category } from "../types.js";

interface FlatRow {
  node: Category;
  depth: number;
}

/** Depth-first flatten used for rendering and for the parent pickers. */
function flatten(nodes: Category[], depth = 0): FlatRow[] {
  return nodes.flatMap((node) => [{ node, depth }, ...flatten(node.children, depth + 1)]);
}

function flattenVisible(nodes: Category[], collapsed: Set<string>, depth = 0): FlatRow[] {
  return nodes.flatMap((node) => [
    { node, depth },
    ...(collapsed.has(node.id) ? [] : flattenVisible(node.children, collapsed, depth + 1)),
  ]);
}

function findCategory(nodes: Category[], id: string): Category | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const child = findCategory(node.children, id);
    if (child) return child;
  }
  return undefined;
}

/** `id` plus every descendant — none of which may become `id`'s new parent. */
function subtreeIds(nodes: Category[], id: string): Set<string> {
  const found = new Set<string>();
  const collect = (node: Category): void => {
    found.add(node.id);
    node.children.forEach(collect);
  };
  const start = findCategory(nodes, id);
  if (start) collect(start);
  return found;
}

/**
 * Category tree management (issue #5): create, rename, re-parent and delete
 * through the existing `/api/categories` endpoints.
 *
 * Two server behaviours surface directly here: duplicate slugs come back as
 * 409 `conflict`, and deletes blocked by children or items come back as 409
 * with a plain-English reason — both are shown verbatim rather than swallowed.
 */
export function CategoriesPage() {
  const categoriesQuery = useCategories();
  const [showCreate, setShowCreate] = useState(false);
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [categoryMessage, setCategoryMessage] = useState<string | null>(null);
  const updateCategory = useUpdateCategory();

  const rows = useMemo(
    () => flattenVisible(categoriesQuery.data ?? [], collapsed),
    [categoriesQuery.data, collapsed],
  );

  function moveUnder(sourceId: string, targetId: string) {
    const tree = categoriesQuery.data ?? [];
    const source = findCategory(tree, sourceId);
    const target = findCategory(tree, targetId);
    if (!source || !target || subtreeIds(tree, sourceId).has(targetId)) return;
    if (!window.confirm(`Move “${source.name}” under “${target.name}”?`)) return;
    setCategoryMessage(null);
    updateCategory.mutate(
      { id: source.id, patch: { name: source.name, slug: source.slug, parentId: target.id } },
      { onSuccess: () => setCategoryMessage(`${source.name} is now under ${target.name}.`) },
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Categories</h1>
          <p className="text-sm text-slate-400">
            Organise inventory into a tree. Items keep their category when you rename or move one.
          </p>
        </div>
        <Button onClick={() => { setCreateParentId(null); setShowCreate(true); }}>New category</Button>
      </div>

      {categoryMessage ? <p role="status" className="text-sm text-emerald-300">{categoryMessage}</p> : null}
      {updateCategory.isError ? <ErrorBanner message={(updateCategory.error as Error).message} /> : null}

      {categoriesQuery.isError ? (
        <ErrorBanner message={(categoriesQuery.error as Error).message} />
      ) : null}

      {categoriesQuery.isLoading ? (
        <Spinner label="Loading categories…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No categories yet"
          hint="Create the first one to start grouping inventory."
        />
      ) : (
        <Card className="max-w-full overflow-x-auto p-0">
          <ul className="min-w-[34rem] divide-y divide-slate-800/60" aria-label="Category tree">
            {rows.map(({ node, depth }) => (
              <li
                key={node.id}
                onDragOver={(event) => { event.preventDefault(); setDragOverId(node.id); }}
                onDragLeave={() => setDragOverId((value) => value === node.id ? null : value)}
                onDrop={(event) => {
                  event.preventDefault();
                  const sourceId = event.dataTransfer.getData("text/category-id") || dragId;
                  setDragId(null);
                  setDragOverId(null);
                  if (sourceId) moveUnder(sourceId, node.id);
                }}
                className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${dragOverId === node.id ? "bg-indigo-950/70" : ""}`}
                style={{ paddingLeft: `${16 + depth * 22}px` }}
              >
                <div className="flex min-w-0 items-start gap-2">
                  {node.children.length ? <button type="button" aria-label={`${collapsed.has(node.id) ? "Expand" : "Collapse"} ${node.name}`} aria-expanded={!collapsed.has(node.id)} onClick={() => setCollapsed((previous) => { const next = new Set(previous); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next; })} className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-800 text-slate-300">{collapsed.has(node.id) ? "›" : "⌄"}</button> : <span className="h-7 w-7 shrink-0" aria-hidden="true" />}
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-800 text-xs font-semibold text-slate-300">{node.name.slice(0,2).toUpperCase()}</span>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-100">{node.name}</div>
                  <div className="truncate font-mono text-xs text-slate-400">
                    /{node.slug}
                    {` · ${node.itemCount ?? 0} item${node.itemCount === 1 ? "" : "s"}`}
                    {node.children.length > 0 ? ` · ${node.children.length} child${node.children.length === 1 ? "" : "ren"}` : ""}
                    {node.whatnotCategoryId !== null ? ` · Whatnot ${node.whatnotCategoryId}` : " · Whatnot unmapped"}
                  </div>
                </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button type="button" draggable onDragStart={(event) => { setDragId(node.id); event.dataTransfer.setData("text/category-id", node.id); event.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { setDragId(null); setDragOverId(null); }} aria-label={`Drag ${node.name} to a new parent; use Edit for keyboard re-parenting`} title="Drag onto another category to move it; use Edit for keyboard re-parenting" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-700 text-slate-300">⠿</button>
                  <Button variant="secondary" onClick={() => { setCreateParentId(node.id); setShowCreate(true); }}>Add child</Button>
                  <Button variant="secondary" onClick={() => setEditTarget(node)}>
                    Edit
                  </Button>
                  <Button variant="danger" disabled={node.children.length > 0 || (node.itemCount ?? 0) > 0} title={node.children.length ? "Remove child categories before deleting" : (node.itemCount ?? 0) > 0 ? "Move or remove assigned items before deleting" : undefined} onClick={() => setDeleteTarget(node)}>
                    {node.children.length || (node.itemCount ?? 0) > 0 ? "In use" : "Delete"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {showCreate ? <CategoryForm initialParentId={createParentId} onClose={() => setShowCreate(false)} /> : null}
      {editTarget ? (
        <CategoryForm category={editTarget} onClose={() => setEditTarget(null)} />
      ) : null}
      {deleteTarget ? (
        <DeleteCategoryModal category={deleteTarget} onClose={() => setDeleteTarget(null)} />
      ) : null}
    </div>
  );
}

function CategoryForm({
  category,
  initialParentId,
  onClose,
}: {
  category?: Category;
  initialParentId?: string | null;
  onClose: () => void;
}) {
  const categoriesQuery = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const mutation = category ? updateCategory : createCategory;

  const [name, setName] = useState(category?.name ?? "");
  const [slug, setSlug] = useState(category?.slug ?? "");
  const [parentId, setParentId] = useState(category?.parentId ?? initialParentId ?? "");
  const [whatnotCategoryId, setWhatnotCategoryId] = useState(category?.whatnotCategoryId?.toString() ?? "");
  const [whatnotCategoryType, setWhatnotCategoryType] = useState(category?.whatnotCategoryType ?? "");
  const [whatnotTaxonomyVersion, setWhatnotTaxonomyVersion] = useState(category?.whatnotTaxonomyVersion ?? "");
  const [whatnotConditionAttributeId, setWhatnotConditionAttributeId] = useState(category?.whatnotConditionAttributeId ?? "");

  const rows = useMemo(() => flatten(categoriesQuery.data ?? []), [categoriesQuery.data]);

  // Never offer the node itself or anything beneath it as a new parent —
  // that is the cycle the API also refuses server-side.
  const blocked = useMemo(
    () => (category ? subtreeIds(rows.map((row) => row.node), category.id) : new Set<string>()),
    [category, rows],
  );

  const parentOptions = rows.filter((row) => !blocked.has(row.node.id));
  const submitting = mutation.isPending;

  return (
    <Modal title={category ? `Edit — ${category.name}` : "New category"} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          const input = {
            name,
            slug: slug || undefined,
            parentId: parentId || null,
            whatnotCategoryId: whatnotCategoryId.trim() ? Number(whatnotCategoryId) : null,
            whatnotCategoryType: whatnotCategoryType.trim() || null,
            whatnotTaxonomyVersion: whatnotTaxonomyVersion.trim() || null,
            whatnotConditionAttributeId: whatnotConditionAttributeId.trim() || null,
          };
          if (category) {
            updateCategory.mutate({ id: category.id, patch: input }, { onSuccess: () => onClose() });
          } else {
            createCategory.mutate(input, { onSuccess: () => onClose() });
          }
        }}
      >
        {mutation.isError ? (
          <ErrorBanner message={(mutation.error as Error).message} />
        ) : null}

        <Field label="Name">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            maxLength={200}
          />
        </Field>

        <Field
          label="Slug"
          hint="Optional. Derived from the name when left blank; must be unique."
        >
          <Input
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            maxLength={200}
            autoCapitalize="none"
            spellCheck={false}
          />
        </Field>

        <Field label="Parent" hint="Root means top level.">
          <Select value={parentId} onChange={(event) => setParentId(event.target.value)}>
            <option value="">— Root —</option>
            {parentOptions.map((row) => (
              <option key={row.node.id} value={row.node.id}>
                {`${row.depth > 0 ? "\u00a0".repeat(row.depth * 2) : ""}${row.node.name}`}
              </option>
            ))}
          </Select>
        </Field>

        <div className="border-t border-slate-800 pt-3">
          <p className="mb-3 text-sm font-medium text-slate-200">Whatnot taxonomy mapping</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Category ID" hint="Stable numeric ID from Whatnot's taxonomy.">
              <Input type="number" min="1" step="1" value={whatnotCategoryId} onChange={(event) => setWhatnotCategoryId(event.target.value)} />
            </Field>
            <Field label="Category slug">
              <Input value={whatnotCategoryType} onChange={(event) => setWhatnotCategoryType(event.target.value)} autoCapitalize="none" spellCheck={false} />
            </Field>
            <Field label="Taxonomy version" hint="Version used to confirm this mapping.">
              <Input value={whatnotTaxonomyVersion} onChange={(event) => setWhatnotTaxonomyVersion(event.target.value)} placeholder="e.g. 1.2.3" />
            </Field>
            <Field label="Condition attribute ID" hint="Optional Whatnot product attribute ID used to send this category's condition value.">
              <Input value={whatnotConditionAttributeId} onChange={(event) => setWhatnotConditionAttributeId(event.target.value)} autoCapitalize="none" spellCheck={false} />
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || name.trim().length === 0}>
            {submitting ? "Saving…" : category ? "Save changes" : "Create category"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteCategoryModal({
  category,
  onClose,
}: {
  category: Category;
  onClose: () => void;
}) {
  const deleteCategory = useDeleteCategory();

  return (
    <Modal title={`Delete ${category.name}?`} onClose={onClose}>
      <div className="space-y-4">
        {/* The API answers 409 with a specific reason when children or items
            block the delete — show it verbatim, that is the whole point. */}
        {deleteCategory.isError ? (
          <ErrorBanner message={(deleteCategory.error as Error).message} />
        ) : null}
        <p className="text-sm text-slate-300">
          This removes <span className="font-medium">{category.name}</span> permanently. The
          API refuses the delete while child categories or live items still reference it, and
          tells you which one is in the way.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleteCategory.isPending}
            onClick={() =>
              deleteCategory.mutate(category.id, {
                onSuccess: () => onClose(),
                // Keep the modal open: the 409 message stays visible.
                onError: () => undefined,
              })
            }
          >
            {deleteCategory.isPending ? "Deleting…" : "Delete category"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
